import { useEffect } from 'react';

import { isStatusTimeline } from 'Utilities/restricted';
import { useGetLatestPostSaleOffer } from 'Utilities/vehicles/hooks/useLatestPostSaleOffer';
import useGetPaymentsStatuses from 'Utilities/vehicles/hooks/usePaymentsStatus';

import { generateVehicleCardActions } from '../../../containers/app/account/Pages/AccountPage/AccountPage.helpers';
import { PROFILING_PATH } from '../../../containers/app/vehicleProfiling/VehicleProfiling.constants';
import { useFeatureFlags } from '../../hooks';
import { Vehicle, VehicleLookupState } from '../@types';

import useGetEnquiryOffers from './useGetEnquiryOffers';
import useGetEnquiryStatus from './useGetEnquiryStatus';

type GetEnquiryStatusAndOffersProps = {
  vehicle?: Vehicle | VehicleLookupState;
}

// eslint-disable-next-line max-len
export const useGetEnquiryStatusAndOffers = ({ vehicle }: GetEnquiryStatusAndOffersProps) => {
  const { underOfferNewFlow } = useFeatureFlags();

  const vrm = vehicle?.vrm;
  const dpEnquiryId = vehicle?.enquiry?.dpEnquiryId;
  const isMarketplaceEligible = Boolean(vehicle?.enquiry?.marketplaceEligible?.eligible);

  const {
    data: enquiryOffers,
    getEnquiryOffers,
    isFetched: isEnquiryOffersFetched,
    isLoading: isEnquiryOffersLoading,
  } = useGetEnquiryOffers();

  const {
    data: enquiryStateSlug,
    getEnquiryStatus,
    isFetched: isEnquiryStatusFetched,
    isLoading: isEnquiryStatusLoading,
    isOfferRestricted,
  } = useGetEnquiryStatus({ dpStateSlug: vehicle?.enquiry?.dpStateSlug });

  const {
    data: latestPostSaleOfferData,
    getLatestPostSaleOffer,
  } = useGetLatestPostSaleOffer();

  const {
    data: paymentsStatuses,
    getPaymentsStatuses,
    isLoading: isPaymentsStatusesLoading,
  } = useGetPaymentsStatuses();

  const { gaEvent, label: primaryCtaLabel, path } = generateVehicleCardActions(
    enquiryStateSlug,
    dpEnquiryId,
  );

  const getLinkToContinueSelling = (): string => {
    if (paymentsStatuses || latestPostSaleOfferData?.type === 'counter_offer') {
      return `/${vrm}/vehicle-payment`;
    }

    let targetPath = path;

    if (enquiryOffers) {
      targetPath = underOfferNewFlow ? '/confirm-sale' : '/accept-offer';
    } else if (isStatusTimeline(enquiryStateSlug)) {
      targetPath = `/profile${PROFILING_PATH.TIMELINE}`;
    }

    return `/${vrm}${targetPath}`;
  };

  useEffect(() => {
    if (!dpEnquiryId || !isMarketplaceEligible) {
      return;
    }

    getEnquiryOffers(dpEnquiryId);
    getEnquiryStatus(dpEnquiryId);
  }, [dpEnquiryId, isMarketplaceEligible, getEnquiryOffers, getEnquiryStatus]);

  useEffect(() => {
    if (!dpEnquiryId || enquiryStateSlug !== 'sold_documents_reviewed') {
      return;
    }

    getLatestPostSaleOffer(dpEnquiryId);
  }, [enquiryStateSlug, getLatestPostSaleOffer, dpEnquiryId]);

  useEffect(() => {
    if (!dpEnquiryId || latestPostSaleOfferData?.state !== 'approved') {
      return;
    }

    getPaymentsStatuses(dpEnquiryId);
  }, [getPaymentsStatuses, latestPostSaleOfferData, dpEnquiryId]);

  return {
    enquiryOffers,
    enquiryStateSlug,
    gaEvent,
    isEnquiryOffersFetched,
    isEnquiryOffersLoading,
    isEnquiryStatusFetched,
    isEnquiryStatusLoading,
    isOfferRestricted,
    isPaymentsStatusesLoading,
    latestPostSaleOfferData,
    linkToContinueSelling: getLinkToContinueSelling(),
    paymentsStatuses,
    primaryCtaLabel,
  };
};
